"use client";

import { Command } from "cmdk";
import { FileText, LoaderCircle, Search } from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { OPEN_COMMAND_PALETTE_EVENT } from "@/components/command-palette-event";
import Link, { useLocale } from "@/components/locale-link";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { catalogTypeLabel } from "@/lib/i18n/catalog-type-labels";
import type { Dictionary } from "@/lib/i18n/dictionaries/en";
import { t } from "@/lib/i18n/interpolate";
import { withLocale } from "@/lib/i18n/link-path";
import {
  groupSpotlightResults,
  SpotlightSearchController,
  type SpotlightSearchResult,
} from "@/lib/spotlight-search";

interface CommandPaletteCopy {
  title: string;
  description: string;
  inputLabel: string;
  inputPlaceholder: string;
  loading: string;
  error: string;
  noMatches: string;
  viewAll: string;
}

type SearchPhase = "idle" | "loading" | "ready" | "error";

interface SearchState {
  phase: SearchPhase;
  query: string;
  results: SpotlightSearchResult[];
}

const INITIAL_SEARCH_STATE: SearchState = {
  phase: "idle",
  query: "",
  results: [],
};

export function CommandPalette({
  catalogTypeLabels,
  copy,
}: {
  catalogTypeLabels: Dictionary["catalogTypes"];
  copy: CommandPaletteCopy;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchState, setSearchState] =
    useState<SearchState>(INITIAL_SEARCH_STATE);
  const [searchController] = useState(() => new SpotlightSearchController());
  const router = useRouter();
  const locale = useLocale();

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (
        !event.repeat &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "k"
      ) {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    }

    function handleOpen() {
      setOpen(true);
    }

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener(OPEN_COMMAND_PALETTE_EVENT, handleOpen);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, handleOpen);
    };
  }, []);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (open && normalizedQuery) {
      setSearchState({
        phase: "loading",
        query: normalizedQuery,
        results: [],
      });
      searchController.search(normalizedQuery, locale, {
        onSuccess(response) {
          setSearchState({
            phase: "ready",
            query: normalizedQuery,
            results: response.results,
          });
        },
        onError() {
          setSearchState({
            phase: "error",
            query: normalizedQuery,
            results: [],
          });
        },
      });
    } else {
      searchController.cancel();
      setSearchState(INITIAL_SEARCH_STATE);
    }
    return () => {
      searchController.cancel();
    };
  }, [locale, open, query, searchController]);

  const navigate = useCallback(
    (href: string) => {
      setOpen(false);
      router.push(withLocale(href, locale));
    },
    [router, locale]
  );

  const normalizedQuery = query.trim();
  let phase: SearchPhase = "idle";
  if (normalizedQuery) {
    phase =
      searchState.query === normalizedQuery ? searchState.phase : "loading";
  }
  const visibleResults =
    phase === "ready" ? searchState.results : INITIAL_SEARCH_STATE.results;
  const groups = groupSpotlightResults(visibleResults);
  const viewAllParams = new URLSearchParams({ q: normalizedQuery });
  const viewAllHref = `/search?${viewAllParams.toString()}`;

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogContent
        className="overflow-hidden p-0 sm:max-w-xl"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{copy.title}</DialogTitle>
        <DialogDescription className="sr-only">
          {copy.description}
        </DialogDescription>
        <Command
          className="[&_[cmdk-group-heading]]:mb-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:text-xs"
          loop
          shouldFilter={false}
        >
          <div className="flex items-center gap-2 border-border border-b px-4">
            <Search
              aria-hidden="true"
              className="size-4 shrink-0 text-muted-foreground"
            />
            <Command.Input
              aria-label={copy.inputLabel}
              autoComplete="off"
              className="flex h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              onValueChange={setQuery}
              placeholder={copy.inputPlaceholder}
              value={query}
            />
          </div>
          <Command.List
            aria-busy={phase === "loading"}
            className="max-h-[min(24rem,60vh)] overflow-y-auto p-2"
          >
            {phase === "idle" ? (
              <p className="px-3 py-8 text-center text-muted-foreground text-sm">
                {copy.description}
              </p>
            ) : null}
            {phase === "loading" ? (
              <output className="flex items-center justify-center gap-2 px-3 py-8 text-muted-foreground text-sm">
                <LoaderCircle
                  aria-hidden="true"
                  className="size-4 animate-spin"
                />
                {copy.loading}
              </output>
            ) : null}
            {phase === "error" ? (
              <p
                className="px-3 py-8 text-center text-destructive text-sm"
                role="alert"
              >
                {copy.error}
              </p>
            ) : null}
            {phase === "ready" && groups.length === 0 ? (
              <output className="block px-3 py-8 text-center text-muted-foreground text-sm">
                {t(copy.noMatches, { query: normalizedQuery })}
              </output>
            ) : null}
            {groups.map((group) => (
              <Command.Group
                heading={catalogTypeLabel(catalogTypeLabels, group.type)}
                key={`${group.type}:${group.results[0]?.localizedPath}`}
              >
                {group.results.map((result) => (
                  <Command.Item
                    className="flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-2 text-sm aria-selected:bg-accent"
                    key={result.localizedPath}
                    onSelect={() => {
                      navigate(result.localizedPath);
                    }}
                    value={result.localizedPath}
                  >
                    <FileText
                      aria-hidden="true"
                      className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                    />
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-foreground">
                        {result.title}
                      </span>
                      <span className="mt-0.5 line-clamp-1 block text-muted-foreground text-xs">
                        {result.description}
                      </span>
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </Command.List>
          {normalizedQuery ? (
            <div className="border-border border-t p-2">
              <Link
                className="flex items-center gap-2 rounded-lg px-2 py-2 font-medium text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                href={viewAllHref}
                onClick={() => {
                  setOpen(false);
                }}
              >
                <Search
                  aria-hidden="true"
                  className="size-4 text-muted-foreground"
                />
                {t(copy.viewAll, { query: normalizedQuery })}
              </Link>
            </div>
          ) : null}
        </Command>
      </DialogContent>
    </Dialog>
  );
}
